import { createContext, useContext } from 'react';
import { api, setAuthToken } from './api';

export interface User {
  id: string;
  email: string;
  display_name: string;
  role: string;
  credit_balance: number;
  tier: { id: string; name: string };
  quota_usage?: {
    daily_generations_used: number;
    daily_generations_limit: number;
    concurrent_jobs_active: number;
    concurrent_jobs_limit: number;
    storage_used_mb: number;
    storage_limit_mb: number;
  };
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, display_name: string) => Promise<void>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export async function loginUser(email: string, password: string): Promise<{ token: string }> {
  const res = await api.post<{ token: string; expires_at: string }>('/auth/login', { email, password });
  return { token: res.data.token };
}

export async function registerUser(
  email: string,
  password: string,
  display_name: string,
): Promise<{ token: string }> {
  const res = await api.post<{ token: string; expires_at: string }>('/auth/register', {
    email,
    password,
    display_name,
  });
  return { token: res.data.token };
}

export async function logoutUser(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    // Ignore errors on logout
  }
  setAuthToken(null);
}

export async function requestPasswordResetApi(email: string): Promise<void> {
  await api.post('/auth/password-reset', { email });
}

export async function resetPasswordApi(token: string, new_password: string): Promise<void> {
  await api.post(`/auth/password-reset/${token}`, { new_password });
}

export async function fetchCurrentUser(): Promise<User> {
  const res = await api.get<User>('/users/me');
  return res.data;
}

export function isAdmin(user: User | null): boolean {
  return user?.role === 'admin' || user?.role === 'super_admin';
}
